import React, { useState } from 'react';
import { Handshake, Building2, UsersRound, BarChart3, ArrowRight } from 'lucide-react';
import scodacLogo from '../../Scodac Logo Approved 1 new one (2).png';
import { api, ApiError } from '../services/api.js';

const FEATURES = [
  { icon: UsersRound, text: 'Track every lead and deal across all your products in one pipeline.', color: '#3D6EF5' },
  { icon: Handshake, text: 'Manage your growing partner network with sourced-pipeline attribution.', color: '#7C3AED' },
  { icon: Building2, text: 'Keep accounts, contacts and opportunities organized in one place.', color: '#D97706' },
  { icon: BarChart3, text: 'See revenue, forecasts and partner performance at a glance.', color: '#16A34A' },
];

export default function Login({ onLogin }) {
  const [step, setStep] = useState('email'); // 'email' | 'code'
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [session, setSession] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const sendCode = async (e) => {
    e.preventDefault();
    const normalized = email.trim().toLowerCase();
    if (!/^[a-z0-9._%+-]+@scodac\.com$/i.test(normalized)) { setError('Use your @scodac.com email address.'); return; }
    setError('');
    setLoading(true);
    try {
      const { session } = await api.auth.startOtp(normalized);
      setSession(session);
      setEmail(normalized);
      setStep('code');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not send a code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async (e) => {
    e.preventDefault();
    if (!code.trim()) { setError('Enter the 8-digit code we emailed you.'); return; }
    setError('');
    setLoading(true);
    try {
      const user = await api.auth.verifyOtp(email, code.trim(), session);
      onLogin(user);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Incorrect or expired code.');
    } finally {
      setLoading(false);
    }
  };

  const backToEmail = () => { setStep('email'); setCode(''); setError(''); };

  return (
    <div className="lh-shell">
      <div className="lh-left">
        <div className="lh-left-inner">
          <div className="lh-hero-logo">
            <img className="lh-hero-logo-image" src={scodacLogo} alt="SCODAC" />
          </div>
          <h1 className="lh-hero-title">Every lead, partner and account — in one place.</h1>
          <p className="lh-hero-desc">Built for teams selling multiple products through a growing partner network. No more spreadsheets.</p>
          <div className="lh-features">
            {FEATURES.map((f) => (
              <div className="lh-feature-card" key={f.text}>
                <span className="lh-feature-icon" style={{ background: `${f.color}18`, color: f.color }}>
                  <f.icon size={18} />
                </span>
                <span className="lh-feature-text">{f.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="lh-right">
        <span className="lh-orb lh-orb--1" />
        <span className="lh-orb lh-orb--2" />
        <div className="lh-card">
          <div className="lh-card-top">
            <div className="lh-card-title">{step === 'email' ? 'Welcome back' : 'Check your email'}</div>
            <div className="lh-card-sub">
              {step === 'email' ? 'Sign in to your SCODAC CRM workspace with a one-time code.' : `We sent an 8-digit code to ${email}.`}
            </div>
          </div>
          {error && <div className="lh-error">{error}</div>}
          {step === 'email' ? (
            <form className="lh-form" onSubmit={sendCode}>
              <div className="lh-field">
                <label className="lh-label">Work email</label>
                <div className="lh-input-wrap">
                  <input className="lh-input" type="email" placeholder="you@scodac.com" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />
                </div>
              </div>
              <button className="lh-submit" type="submit" disabled={loading}>
                {loading ? <span className="lh-spinner" /> : <>Send code <ArrowRight size={15} /></>}
              </button>
            </form>
          ) : (
            <form className="lh-form" onSubmit={verifyCode}>
              <div className="lh-field">
                <label className="lh-label">8-digit code</label>
                <div className="lh-input-wrap">
                  <input className="lh-input" inputMode="numeric" autoComplete="one-time-code" maxLength={8} placeholder="12345678" value={code} onChange={(e) => setCode(e.target.value)} autoFocus />
                </div>
              </div>
              <button className="lh-submit" type="submit" disabled={loading}>
                {loading ? <span className="lh-spinner" /> : <>Verify & sign in <ArrowRight size={15} /></>}
              </button>
              <button type="button" className="lh-forgot" style={{ marginTop: 10 }} onClick={backToEmail}>Use a different email</button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
